import throttle from "lodash.throttle";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector.ts";
import { enableAnalytics } from "@ch/api";

export const inactivityTimeout = (import.meta.env.DEV ? 30 : 3) * 60 * 1000; // 3 minute(s) in production

export interface UseActivityObserver {
  onInactive?: () => void;
  routeNamesToSkip?: string[];
  isDisabled?: boolean;
}

const useActivityObserver = ({
  onInactive,
  routeNamesToSkip,
  isDisabled,
}: UseActivityObserver = {}) => {
  const user = useAppSelector((state) => state?.user.currentUser);
  const [isActive, setIsActive] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const currentRouteRef = useRef<string | undefined>(undefined);

  const location = useLocation();

  const startTimeoutObserver = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIsActive(false);
      onInactive?.();
    }, inactivityTimeout);
  }, [onInactive]);

  const stopTimeoutObserver = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsActive(true);
  }, []);

  const resetTimeoutObserver = useCallback(() => {
    stopTimeoutObserver();
    startTimeoutObserver();
  }, [stopTimeoutObserver, startTimeoutObserver]);

  const shouldSkipRoute = useCallback(() => {
    if (!routeNamesToSkip || !currentRouteRef?.current) {
      return false;
    }

    return routeNamesToSkip.includes(currentRouteRef.current);
  }, [routeNamesToSkip]);

  const triggerActivity = useCallback(() => {
    if (!isActive || isDisabled) {
      return false;
    }

    stopTimeoutObserver();

    // Do not start timeout if route should be skipped and is not disabled
    if (enableAnalytics && !shouldSkipRoute() && !user?.idleLogoutDisabled) {
      startTimeoutObserver();
    }

    return false;
  }, [
    isActive,
    isDisabled,
    shouldSkipRoute,
    startTimeoutObserver,
    stopTimeoutObserver,
    user?.idleLogoutDisabled,
  ]);

  const throttledTriggerActivity = throttle(triggerActivity, 2000);

  // Listen for inactivity
  useEffect(() => {
    if (!isActive || isDisabled) {
      stopTimeoutObserver();
    }
  }, [isActive, isDisabled, stopTimeoutObserver]);

  // Listen for route changes
  useEffect(() => {
    if (isDisabled) {
      return;
    }

    currentRouteRef.current = location.pathname;

    triggerActivity();
  }, [
    isDisabled,
    location.pathname,
    shouldSkipRoute,
    startTimeoutObserver,
    stopTimeoutObserver,
    triggerActivity,
  ]);

  return {
    isActive,
    startTimeoutObserver,
    stopTimeoutObserver,
    shouldSkipRoute,
    resetTimeoutObserver,
    triggerActivity,
    throttledTriggerActivity,
  };
};

export default useActivityObserver;
